import { FormEvent, useEffect, useState } from 'react';
import { Button, Card, CardBody, Col, Form, FormGroup, Input, Label, Row } from 'reactstrap';

import CommonInput from '../../common/CommonInput';
import CommonCardHeader from '../../../../../coreComponents/CommonCardHeader';
import { BaseInput, EditLessonInput } from '../../../../../types/Forms';
import Loader from '../../../../../layout/Loader';
interface FromButton {
  type: string;
  title: string;
  color?: string;
  outline?: string;
  clickHandler?: () => void;
  disabled?: boolean;
  disabledTitle?: string;
}

type FormFieldValue = string | number | FileList | undefined;

interface Props<T extends Record<string, FormFieldValue>> {
  inputs: BaseInput<any>[];
  formTitle: string;
  formSubTitle: string;
  submitHandler: (formData: T) => void;
  buttons: FromButton[];
  data?: T;
}

const BasicForm = <T extends Record<string, FormFieldValue>>({
  inputs,
  formTitle,
  formSubTitle,
  submitHandler,
  buttons,
  data,
}: Props<T>) => {
  const [formData, setFormData] = useState<T>(() => {
    const initialState = {} as T;
    inputs.forEach((input) => {
      (initialState as any)[input.slug] = input.value;
    });
    return initialState;
  });
  const [validationErrors, setValidationErrors] = useState<Record<string, string | null>>({});
  const [formIsValid, setFormIsValid] = useState(false);
  useEffect(() => {
    setFormIsValid(Object.values(validationErrors).every((e) => !e));
  }, [validationErrors]);

  const handleChange = (field: keyof T, value: string | FileList | null) => {
    const newValue = value === null ? undefined : value;
    setFormData((prev) => ({
      ...prev,
      [field]: newValue,
    }));

    const inputConfig = inputs.find((i) => i.slug === field);
    if (inputConfig?.validator) {
      const error = inputConfig.validator(newValue as any);
      console.log('error', error);
      setValidationErrors((prev) => ({
        ...prev,
        [field]: typeof error === 'boolean' ? (error ? 'Invalid value' : null) : error,
      }));
    } else {
      setValidationErrors((prev) => ({
        ...prev,
        [field]: null, // clear if no validator
      }));
    }
  };

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const errors: string[] = [];

    inputs.forEach((input) => {
      const value = formData[input.slug as keyof T];

      if (
        input.required &&
        (value === undefined || value === '' || (value instanceof FileList && value.length === 0))
      ) {
        errors.push(`${input.title || input.slug} is required`);
      }

      if (input.validator) {
        const validationResult = input.validator(value as string | boolean | null);
        if (validationResult) {
          const msg = input.title || input.slug;
          errors.push(`${msg} not valid`);
        }
      }
    });

    if (errors.length > 0) {
      alert(errors.join('\n'));
      return;
    }
    submitHandler(formData);
  };

  const renderRadioGroup = (slug: string, groupItems: BaseInput<any>[]) => {
    return (
      <Col md="12">
        <Label> {groupItems[0].groupTitle || ''}</Label>
        {groupItems.map((input) => (
          <FormGroup check key={input.id} className={`radio radio-${input.color || 'primary'}`}>
            <Input
              type="radio"
              name={slug}
              id={`radio-${input.id}`}
              value={input.value}
              checked={formData[slug as keyof T] === input.value}
              onChange={() => handleChange(slug as keyof T, input?.value ?? '')}
              disabled={input.disabled}
            />
            <Label check htmlFor={`radio-${input.id}`}>
              {input.text}
            </Label>
          </FormGroup>
        ))}
      </Col>
    );
  };

  // Group radio buttons by slug
  const radioGroups = inputs
    .filter((i) => i.type === 'radio')
    .reduce(
      (acc, curr) => {
        if (!acc[curr.slug]) acc[curr.slug] = [];
        acc[curr.slug].push(curr);
        return acc;
      },
      {} as Record<string, BaseInput<any>[]>,
    );

  const nonRadioInputs = inputs.filter((i) => i.type !== 'radio');
  return (
    <Col md="12">
      <Card>
        <CommonCardHeader title={formTitle} span={[{ text: formSubTitle }]} />
        <CardBody>
          <div className="card-wrapper border rounded-3">
            <Form onSubmit={handleSubmit}>
              <Row className="g-3">
                {nonRadioInputs.map((el) => (
                  <CommonInput
                    key={el.slug}
                    title={el.title}
                    placeholder={el.placeholder}
                    inputType={el.type}
                    value={
                      el.type === 'file'
                        ? undefined
                        : (formData[el.slug as keyof T] as string | undefined)
                    }
                    colClass="col-md-12"
                    onChange={(e) => {
                      const value = el.type === 'file' ? e.target.files : e.target.value;
                      handleChange(el.slug as keyof T, value);
                    }}
                    required={el.required}
                    disabled={el.disabled}
                  />
                ))}

                {/* Render radio groups */}
                {Object.entries(radioGroups).map(([slug, groupItems]) =>
                  renderRadioGroup(slug, groupItems),
                )}

                <Col xs="12">
                  {buttons.map((button, idx) => (
                    <Button
                      key={idx}
                      type={button.type === 'submit' ? 'submit' : 'button'}
                      color={button.color}
                      outline={!!button.outline}
                      onClick={() => {
                        button.clickHandler?.();
                      }}
                      disabled={
                        button.type === 'submit'
                          ? button.disabled || !formIsValid // disable if form invalid
                          : button.disabled
                      }
                      className="mr-2"
                    >
                      {button.disabled && button.disabledTitle
                        ? button.disabledTitle
                        : button.title}
                    </Button>
                  ))}
                </Col>
              </Row>
            </Form>
          </div>
        </CardBody>
      </Card>
    </Col>
  );
};

export default BasicForm;
